import {Skeleton} from '@mantine/core';
import {useQueryClient} from '@tanstack/react-query';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type React from 'react';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {ENTRY_MANAGEMENTS_PROGRESS} from '../../../config/constants/routes';
import {
  useGetProgressMissions,
  useGetReminders,
  useUpdateEntryManagement,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useGetDepartmentsInSummary} from '../../../core/repositories/reportSummaryRepository';
import {showErrorSnackBar} from '../../../shared/components';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {
  ENTRY_MANAGEMENT_DESCRIPTION_PROGRESS,
  ENTRY_MANAGEMENT_TITLE_PROGRESS,
} from '../constants/metaData';
import {QUERY_MODE_ADD_MANAGEMENT} from '../constants/queryParams';
import {buildQueryStringWithout} from '../usecase/entryManagementQueryParamUtils';
import {generateReminderText} from '../usecase/generateReminderText';
import {useCompanySearch} from '../usecase/useCompanySearch';
import {
  CACHE_KEYS,
  ERROR_MESSAGES as UPDATE_ERROR_MESSAGES,
  useEntryManagementUpdate,
} from '../usecase/useEntryManagementUpdate';
import {useProgressMgtData} from '../usecase/useProgressMgtData';
import {useProgressOverlays} from '../usecase/useProgressOverlays';
import {
  convertSpStatusToFilterValues,
  useProgressStatusFilter,
} from '../usecase/useProgressStatusFilter';
import {useRequireAuth} from '../usecase/useRequireAuth';
import {useRequireStudentVerification} from '../usecase/useRequireStudentVerification';
import {useSortAndFilter} from '../usecase/useSortAndFilter';
import {useTodosData} from '../usecase/useTodosData';
import {AiTodoGenerateWithEntryMgtCreationContainer} from './Container/Progress/AiTodoGenerateWithEntryMgtCreationContainer';
import {ProgressOverlays} from './Container/Progress/ProgressOverlays';
import {
  type ProgressPageComponentProps,
  ProgressPagePcComponent,
} from './Container/Progress/ProgressPagePcComponent';
import {ProgressPageSpComponent} from './Container/Progress/ProgressPageSpComponent';
import {useGuideModal} from './GuideModalContext';

type Props = Record<string, never>;

export const EntryManagementsProgressPage: React.FC<Props> = () => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const {id} = router.query;

  // クエリパラメータからエントリー管理IDを取得（空文字や不正な値はundefined）
  const parsedEntryManagementId =
    typeof id === 'string' && id.trim() !== '' ? Number(id) : Number.NaN;
  const requestedEntryManagementId = Number.isFinite(parsedEntryManagementId)
    ? parsedEntryManagementId
    : undefined;

  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const {isPC} = useMediaQuery();
  const {isAuthenticated, isAuthResolved, withRequireAuth, requireAuth} =
    useRequireAuth();
  const {
    isStudentVerified,
    withRequireStudentVerification,
    requireStudentVerification,
  } = useRequireStudentVerification();

  // 大学未認証のユーザーには詳細を開かせない
  const entryManagementId = isStudentVerified
    ? requestedEntryManagementId
    : undefined;

  const {sortBy, sortOrder, filterValues, handleSort, handleFilter} =
    useSortAndFilter();

  // SP版のステータスフィルター（PCでは使用しないが、hooks順序を保つため常に呼び出す）
  const {selectedStatus, handleStatusChange} = useProgressStatusFilter();

  // SP用のフィルター値をマージ（PCは既存のfilterValuesをそのまま使用）
  const mergedFilterValues = useMemo(() => {
    if (isPC) return filterValues;
    const spStatusFilter = convertSpStatusToFilterValues(selectedStatus);
    return {
      ...filterValues,
      ...spStatusFilter,
    };
  }, [isPC, filterValues, selectedStatus]);

  const [companySearchWord, setCompanySearchWord] = useState('');
  const [confirmedCompanyName, setConfirmedCompanyName] = useState('');

  const handleCompanySearchSubmit = useCallback((value: string) => {
    setConfirmedCompanyName(value);
  }, []);

  const {
    data,
    allData,
    totalCount,
    entryDetailData,
    nextStepName,
    existingCompanyIds,
    existingCompanyNames,
    selectedCompanyId,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useProgressMgtData({
    entryManagementId,
    sortBy,
    sortOrder,
    filterValues: mergedFilterValues,
    companyName: confirmedCompanyName,
    isSp: !isPC,
    isAuthenticated,
  });

  const {companyList, displayEntries} = useCompanySearch({
    allData: allData?.ok ? allData.value : undefined,
    searchData: data?.ok ? data.value : undefined,
    paginatedData: data?.ok ? data.value : undefined,
    companySearchWord: confirmedCompanyName,
  });

  const updateMutation = useUpdateEntryManagement();
  const queryClient = useQueryClient();

  const onUpdateEntryStatus = useCallback(
    async (params: {
      id: number;
      overallStatus: string;
      hasCompletedEntry?: boolean;
    }) => {
      const targetEntry = data?.ok
        ? data.value.entries.find((entry) => entry.id === params.id)
        : undefined;

      if (!targetEntry) {
        showErrorSnackBar({message: '更新対象のエントリーが見つかりません'});
        return;
      }

      try {
        await updateMutation.mutateAsync({
          id: params.id,
          companyID: targetEntry.companyID || 0,
          companyName: targetEntry.companyName,
          overallStatus: params.overallStatus,
          hasCompletedEntry: params.hasCompletedEntry,
        });

        const invalidationPromises = [
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
          }),
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
          }),
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT, params.id],
          }),
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT_REMINDERS],
          }),
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.PROGRESS_MISSIONS],
          }),
        ];
        await Promise.all(invalidationPromises);
      } catch (error) {
        showErrorSnackBar(UPDATE_ERROR_MESSAGES.OVERALL_STATUS_UPDATE);
        throw error;
      }
    },
    [data?.ok, data?.value?.entries, updateMutation, queryClient],
  );

  const companyID =
    entryDetailData?.ok && entryDetailData.value.companyID
      ? entryDetailData.value.companyID
      : null;

  const departmentsData = useGetDepartmentsInSummary({
    companyID: companyID ?? 0,
    queryParams: {reportType: 'selection'},
    config: {
      enabled: !!companyID && companyID > 0,
    },
  });

  const modalProps = useProgressOverlays(companyID, departmentsData);

  const {updateOverallStatus: handleUpdateOverallStatus} =
    useEntryManagementUpdate({
      entryManagementId: entryManagementId ?? 0,
      companyId: entryDetailData?.ok ? entryDetailData.value.companyID || 0 : 0,
      companyName: entryDetailData?.ok ? entryDetailData.value.companyName : '',
    });

  const {data: remindersData} = useGetReminders({
    config: {enabled: isAuthenticated},
  });
  const reminderText =
    remindersData?.ok && remindersData.value
      ? generateReminderText(remindersData.value)
      : {deadlineText: undefined, eventText: undefined};
  const reminderTexts = [
    reminderText.deadlineText,
    reminderText.eventText,
  ].filter(Boolean) as string[];

  const [mounted, setMounted] = useState(false);
  const [isAiTodoModalOpen, setIsAiTodoModalOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {open: openGuideModal, onAddEntryRef} = useGuideModal();
  const hasShownGuideModal = useRef(false);
  const hasOpenedModalFromQuery = useRef(false);

  useEffect(() => {
    onAddEntryRef.current = modalProps.handleAddEntryManagement;
    return () => {
      onAddEntryRef.current = undefined;
    };
  }, [modalProps.handleAddEntryManagement, onAddEntryRef]);

  // ガイドモーダル表示制御: 未ログイン時または0件時に表示
  useEffect(() => {
    if (hasShownGuideModal.current || confirmedCompanyName) return;

    // 認証状態が確定するまで判定をスキップ
    if (!isAuthResolved) return;

    // 未ログイン時はガイドモーダルを表示
    if (!isAuthenticated) {
      hasShownGuideModal.current = true;
      openGuideModal();
      return;
    }

    // ログイン時はallDataの取得結果を待ってから判定する
    if (allData === undefined) return;

    // ログイン時は0件の場合にガイドモーダルを表示
    if (allData.ok && allData.value.entries.length === 0) {
      hasShownGuideModal.current = true;
      openGuideModal();
    }
  }, [
    allData,
    confirmedCompanyName,
    openGuideModal,
    isAuthResolved,
    isAuthenticated,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refで管理されているため初回のみ実行される
  useEffect(() => {
    if (
      router.query.mode === QUERY_MODE_ADD_MANAGEMENT &&
      !hasOpenedModalFromQuery.current
    ) {
      hasOpenedModalFromQuery.current = true;
      modalProps.handleAddEntryManagement();
    }
  }, [router.query.mode]);

  const {data: progressMissionsData} = useGetProgressMissions({
    config: {enabled: isAuthenticated},
  });
  const progressMissions = progressMissionsData?.ok
    ? progressMissionsData.value
    : [];

  const {hasTodosForEntryManagement} = useTodosData({isAuthenticated});

  // 大学未認証のユーザーが詳細を開こうとした場合はidだけ除去してモーダルで案内する
  useEffect(() => {
    if (!requestedEntryManagementId || isStudentVerified) return;
    requireStudentVerification();

    // WebViewではrouterの遷移が反映されないためlocation.hrefで置き換える
    if (isWebView) {
      const search = buildQueryStringWithout(router.query, 'id');
      location.href = search
        ? `${ENTRY_MANAGEMENTS_PROGRESS}?${search}`
        : ENTRY_MANAGEMENTS_PROGRESS;
      return;
    }

    const {id: _removed, ...restQuery} = router.query;
    void router.replace(
      {pathname: router.pathname, query: restQuery},
      undefined,
      {shallow: true},
    );
  }, [
    requestedEntryManagementId,
    isStudentVerified,
    requireStudentVerification,
    isWebView,
    router,
  ]);

  // 未ログイン時はスケルトンを表示せず、0件状態のUIを表示
  if (!mounted || (isAuthenticated && isLoading)) {
    return (
      <div className="flex flex-col gap-16">
        <Skeleton height={88} radius="md" />
        <Skeleton height={667} radius="md" />
      </div>
    );
  }

  const componentProps: ProgressPageComponentProps = {
    entryManagementId,
    companySearchWord,
    setCompanySearchWord: withRequireAuth(setCompanySearchWord),
    onCompanySearchSubmit: withRequireAuth(handleCompanySearchSubmit),
    handleSort,
    requireAuth,
    handleFilter: withRequireAuth(handleFilter),
    companyList,
    filteredEntries: displayEntries,
    totalCount,
    reminderTexts,
    handleAddEntryManagement: withRequireAuth(
      withRequireStudentVerification(modalProps.handleAddEntryManagement),
    ),
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    progressMissions,
    onUpdateEntryStatus: async (params) => {
      if (!requireAuth()) return;
      if (!requireStudentVerification()) return;
      await onUpdateEntryStatus(params);
    },
    selectedStatus,
    onStatusChange: withRequireAuth(
      withRequireStudentVerification(handleStatusChange),
    ),
    handleAiTodoGenerate: withRequireAuth(
      withRequireStudentVerification(() => setIsAiTodoModalOpen(true)),
    ),
    canOpenDetail: () => requireAuth() && requireStudentVerification(),
  };

  const Component = isPC ? ProgressPagePcComponent : ProgressPageSpComponent;

  const handleCloseDrawer = () => {
    if (isWebView) {
      location.href = ENTRY_MANAGEMENTS_PROGRESS;
    } else {
      router.push(ENTRY_MANAGEMENTS_PROGRESS);
    }
  };

  return (
    <>
      <Head>
        <title key="title">{ENTRY_MANAGEMENT_TITLE_PROGRESS}</title>
        <meta
          property="og:title"
          key="og:title"
          content={ENTRY_MANAGEMENT_TITLE_PROGRESS}
        />
        <meta
          name="description"
          key="description"
          content={ENTRY_MANAGEMENT_DESCRIPTION_PROGRESS}
        />
        <meta
          property="og:description"
          key="og:description"
          content={ENTRY_MANAGEMENT_DESCRIPTION_PROGRESS}
        />
        <link rel="canonical" href={currentUrl} />
      </Head>
      <Component {...componentProps} />
      <AiTodoGenerateWithEntryMgtCreationContainer
        opened={isAiTodoModalOpen}
        onClose={() => setIsAiTodoModalOpen(false)}
      />
      <ProgressOverlays
        modals={modalProps.modals}
        deleteModal={modalProps.deleteModal}
        isAddingEntryManagement={modalProps.isAddingEntryManagement}
        selectedCompanyId={selectedCompanyId}
        entryManagementId={entryManagementId}
        existingCompanyIds={existingCompanyIds}
        existingCompanyNames={existingCompanyNames}
        entryDetailData={entryDetailData}
        stepDrawerRef={modalProps.stepDrawerRef}
        stepCandidatePreviewModalRef={modalProps.stepCandidatePreviewModalRef}
        handleMethodSelected={modalProps.handleMethodSelected}
        handleSaveCandidate={modalProps.handleSaveCandidate}
        aiTodoGeneration={modalProps.aiTodoGeneration}
        handleOpenDeleteModal={modalProps.handleOpenDeleteModal}
        handleConfirmDelete={modalProps.handleConfirmDelete}
        handleConfirmDeleteEntry={modalProps.handleConfirmDeleteEntry}
        onUpdateOverallStatus={handleUpdateOverallStatus}
        handleReportAnalyzeSuccess={modalProps.handleReportAnalyzeSuccess}
        hasReportData={modalProps.hasReportData}
        handleAddStep={modalProps.handleAddStep}
        handleEditEntry={modalProps.handleEditEntry}
        handleDeleteEntry={modalProps.handleDeleteEntry}
        handleOpenOverallStatusUpdateModal={
          modalProps.handleOpenOverallStatusUpdateModal
        }
        nextStepName={nextStepName}
        isSp={!isPC}
        detailDrawerOpened={!!entryManagementId}
        onCloseDetailDrawer={handleCloseDrawer}
        hasTodosForEntryManagement={hasTodosForEntryManagement}
      />
    </>
  );
};
